import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PLATFORMS } from '@insurensa/shared';
import { Badge, Card, PageTitle } from '@insurensa/ui';
import { useFormat, useT } from '@insurensa/i18n';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { BlockSkeleton, ErrorAlert } from '../components/ui.js';

interface AppRelease {
  id: string;
  platform: string;
  version: string;
  minimumVersion: string;
  updateAvailable: boolean;
  forceUpdate: boolean;
  isActive: boolean;
  releaseNotes: string | null;
  storeUrl: string | null;
  createdAt: string;
}

/**
 * Replaces app_updates.php and updateappstatus.php.
 *
 * The legacy table held one row for both platforms and no minimum version, so a
 * forced update could not be targeted at iOS alone and the app decided for
 * itself whether it was too old.
 */
export function AppReleasesPage(): JSX.Element {
  const t = useT();
  const fmt = useFormat();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [form, setForm] = useState({
    platform: 'ANDROID',
    version: '',
    minimumVersion: '',
    releaseNotes: '',
    updateAvailable: true,
    forceUpdate: false,
  });

  const { data, error, isLoading } = useQuery({
    queryKey: ['app-releases'],
    queryFn: () => api.get<AppRelease[]>('/admin/app-releases'),
  });

  const create = useMutation({
    mutationFn: (input: typeof form) =>
      api.post('/admin/app-releases', {
        platform: input.platform,
        version: input.version,
        minimumVersion: input.minimumVersion,
        releaseNotes: input.releaseNotes || undefined,
        updateAvailable: input.updateAvailable,
        forceUpdate: input.forceUpdate,
        isActive: true,
      }),
    onSuccess: () => {
      setForm({ ...form, version: '', minimumVersion: '', releaseNotes: '' });
      void queryClient.invalidateQueries({ queryKey: ['app-releases'] });
    },
  });

  const toggle = useMutation({
    mutationFn: (input: { id: string; patch: Partial<AppRelease> }) =>
      api.patch(`/admin/app-releases/${input.id}`, input.patch),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['app-releases'] }),
  });

  const canEdit = can('SUPER_ADMIN', 'ADMIN');

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    create.mutate(form);
  };

  return (
    <>
      <PageTitle
        heading={t('admin.appReleases.heading')}
        subheading={t('admin.appReleases.subheading')}
        icon="pe-7s-phone"
      />

      <ErrorAlert error={error} />
      <ErrorAlert error={create.error} />
      <ErrorAlert error={toggle.error} />

      <div className="row">
        <div className={canEdit ? 'col-lg-8' : 'col-12'}>
          <Card
            title={<div className="card-header-title">{t('admin.appReleases.published')}</div>}
            noPadding
          >
            {isLoading ? (
              <div className="p-3">
                <BlockSkeleton height={90} />
              </div>
            ) : data && data.length > 0 ? (
              <table className="table table-hover table-striped mb-0">
                <thead>
                  <tr>
                    <th>{t('admin.appReleases.platform')}</th>
                    <th>{t('admin.appReleases.version')}</th>
                    <th>{t('admin.appReleases.minimumVersion')}</th>
                    <th>{t('common.status')}</th>
                    <th>{t('admin.appReleases.createdAt')}</th>
                    {canEdit && <th />}
                  </tr>
                </thead>
                <tbody>
                  {data.map((release) => (
                    <tr key={release.id}>
                      <td>{release.platform === 'ANDROID' ? 'Android' : 'iOS'}</td>
                      <td>{release.version}</td>
                      <td>{release.minimumVersion}</td>
                      <td>
                        <Badge tone={release.isActive ? 'success' : 'secondary'}>
                          {release.isActive ? t('common.active') : t('common.inactive')}
                        </Badge>
                        {release.forceUpdate && (
                          <Badge tone="danger">
                            <span className="ml-1">{t('admin.appReleases.forced')}</span>
                          </Badge>
                        )}
                      </td>
                      <td>{fmt.dateTime(release.createdAt)}</td>
                      {canEdit && (
                        <td>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled={toggle.isPending}
                            onClick={() =>
                              toggle.mutate({ id: release.id, patch: { isActive: !release.isActive } })
                            }
                          >
                            {release.isActive ? t('common.deactivate') : t('common.activate')}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-4 text-center text-muted">{t('admin.appReleases.empty')}</div>
            )}
          </Card>
        </div>

        {canEdit && (
          <div className="col-lg-4">
            <Card title={<div className="card-header-title">{t('admin.appReleases.addVersion')}</div>}>
              <form onSubmit={onSubmit}>
                <div className="position-relative form-group">
                  <label htmlFor="platform">{t('admin.appReleases.platform')}</label>
                  <select
                    id="platform"
                    className="form-control"
                    value={form.platform}
                    onChange={(event) => setForm({ ...form, platform: event.target.value })}
                  >
                    {PLATFORMS.map((platform) => (
                      <option key={platform} value={platform}>
                        {platform === 'ANDROID' ? 'Android' : 'iOS'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="position-relative form-group">
                  <label htmlFor="version">{t('admin.appReleases.version')}</label>
                  <input
                    id="version"
                    type="text"
                    className="form-control"
                    placeholder="1.2.3"
                    required
                    value={form.version}
                    onChange={(event) => setForm({ ...form, version: event.target.value })}
                  />
                </div>

                <div className="position-relative form-group">
                  <label htmlFor="minimumVersion">{t('admin.appReleases.minimumVersion')}</label>
                  <input
                    id="minimumVersion"
                    type="text"
                    className="form-control"
                    placeholder="1.0.0"
                    required
                    value={form.minimumVersion}
                    onChange={(event) => setForm({ ...form, minimumVersion: event.target.value })}
                  />
                  <small className="form-text text-muted">
                    {t('admin.appReleases.minimumVersionHint')}
                  </small>
                </div>

                <div className="position-relative form-group">
                  <label htmlFor="notes">{t('admin.appReleases.releaseNotes')}</label>
                  <textarea
                    id="notes"
                    className="form-control"
                    rows={3}
                    value={form.releaseNotes}
                    onChange={(event) => setForm({ ...form, releaseNotes: event.target.value })}
                  />
                </div>

                <div className="custom-checkbox custom-control mb-2">
                  <input
                    type="checkbox"
                    id="updateAvailable"
                    className="custom-control-input"
                    checked={form.updateAvailable}
                    onChange={(event) => setForm({ ...form, updateAvailable: event.target.checked })}
                  />
                  <label className="custom-control-label" htmlFor="updateAvailable">
                    {t('admin.appReleases.showUpdatePrompt')}
                  </label>
                </div>

                <div className="custom-checkbox custom-control mb-3">
                  <input
                    type="checkbox"
                    id="forceUpdate"
                    className="custom-control-input"
                    checked={form.forceUpdate}
                    onChange={(event) => setForm({ ...form, forceUpdate: event.target.checked })}
                  />
                  <label className="custom-control-label" htmlFor="forceUpdate">
                    {t('admin.appReleases.forceUpdate')}
                  </label>
                </div>

                <button type="submit" className="btn btn-primary" disabled={create.isPending}>
                  {create.isPending ? t('common.saving') : t('admin.appReleases.submit')}
                </button>
              </form>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
